class PusherService {
  constructor() {
    this.pusher = null;
    this.isInitialized = false;
    this.appKey = import.meta.env.VITE_PUSHER_APP_KEY || 'c5ad4db35e7dca938239';
    this.cluster = import.meta.env.VITE_PUSHER_APP_CLUSTER || 'ap2';
    this.connectionListenersBound = false;
  }

  async initialize() {
    if (this.isInitialized) {
      return this.pusher;
    }

    try {
      // Check if we have valid credentials
      if (!this.appKey || this.appKey === 'your-pusher-app-key') {
        console.warn('Pusher credentials not configured - skipping initialization');
        return null;
      }

      // Load Pusher Channels SDK dynamically
      if (!window.Pusher) {
        await this.loadSDK();
      }

      // Pusher Channels expects (key, options)
      this.pusher = new window.Pusher(this.appKey, {
        cluster: this.cluster,
        forceTLS: true,
      });

      this.isInitialized = true;
      this.bindConnectionEvents();
      
      console.log('Pusher Channels initialized successfully');
      return this.pusher;
    } catch (error) {
      console.error('Failed to initialize Pusher Channels:', error);
      // Don't throw error, just return null so the app continues to work
      return null;
    }
  }

  bindConnectionEvents() {
    if (!this.pusher || this.connectionListenersBound) return;
    this.connectionListenersBound = true;
    this.pusher.connection.bind('connected', () => console.log('Pusher connected'));
    this.pusher.connection.bind('disconnected', () => console.warn('Pusher disconnected'));
    this.pusher.connection.bind('failed', () => console.error('Pusher connection failed'));
    this.pusher.connection.bind('unavailable', () => console.warn('Pusher unavailable'));
    this.pusher.connection.bind('error', (err) => console.error('Pusher error', err));
  }

  loadSDK() {
    return new Promise((resolve, reject) => {
      if (window.Pusher) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://js.pusher.com/8.2.0/pusher.min.js';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  async subscribeToChannel(channelName) {
    if (!this.pusher) {
      this.pusher = await this.initialize();
    }

    if (!this.pusher) {
      console.warn('Pusher not available - skipping subscription');
      return null;
    }

    try {
      const channel = this.pusher.subscribe(channelName);
      console.log(`Successfully subscribed to channel: ${channelName}`);
      return channel;
    } catch (error) {
      console.error(`Failed to subscribe to channel ${channelName}:`, error);
      return null;
    }
  }

  async unsubscribeFromChannel(channelName) {
    if (!this.pusher) {
      return;
    }

    try {
      this.pusher.unsubscribe(channelName);
      console.log(`Successfully unsubscribed from channel: ${channelName}`);
    } catch (error) {
      console.error(`Failed to unsubscribe from channel ${channelName}:`, error);
    }
  }

  async subscribeToUser(userId) {
    const channelName = `user-${userId}`;
    return await this.subscribeToChannel(channelName);
  }

  async subscribeToVendor(vendorId) {
    const channelName = `vendor-${vendorId}`;
    return await this.subscribeToChannel(channelName);
  }

  async subscribeToLead(leadId) {
    const channelName = `lead-${leadId}`;
    return await this.subscribeToChannel(channelName);
  }

  async clearAllSubscriptions() {
    if (!this.pusher) {
      return;
    }

    try {
      // Pusher automatically handles unsubscribing when disconnecting
      this.pusher.disconnect();
      console.log('Successfully cleared all subscriptions');
    } catch (error) {
      console.error('Failed to clear all subscriptions:', error);
    }
  }

  getConnectionState() {
    if (!this.pusher) {
      return 'disconnected';
    }
    return this.pusher.connection.state;
  }

  async notifyTyping(leadId, userId, isTyping) {
    // This is a placeholder for typing notifications
    // In a real implementation, you'd send this to your backend
    // which would then broadcast to other users in the lead chat
    console.log(`Typing notification: lead ${leadId}, user ${userId}, typing: ${isTyping}`);
  }

  // Listen for new lead notifications
  async onNewLead(callback) {
    if (!this.pusher) {
      this.pusher = await this.initialize();
    }
    
    if (!this.pusher) {
      console.warn('Pusher not available - cannot listen for new leads');
      return null;
    }

    try {
      // Get current user ID from localStorage
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user.id) {
        console.warn('User ID not found - cannot listen for new leads');
        return null;
      }

      const channel = await this.subscribeToChannel(`user-${user.id}`);
      channel.bind('new-lead', callback);
      return channel;
    } catch (error) {
      console.error('Failed to listen for new leads:', error);
      return null;
    }
  }

  // Listen for booking status updates
  async onBookingUpdate(callback) {
    if (!this.pusher) {
      this.pusher = await this.initialize();
    }
    
    if (!this.pusher) {
      console.warn('Pusher not available - cannot listen for booking updates');
      return null;
    }

    try {
      const channel = await this.subscribeToChannel('booking-updates');
      channel.bind('booking-updated', callback);
      return channel;
    } catch (error) {
      console.error('Failed to listen for booking updates:', error);
      return null;
    }
  }

  // Listen for new messages
  async onNewMessage(callback) {
    if (!this.pusher) {
      this.pusher = await this.initialize();
    }
    
    if (!this.pusher) {
      console.warn('Pusher not available - cannot listen for new messages');
      return null;
    }

    try {
      const channel = await this.subscribeToChannel('messages');
      channel.bind('new-message', callback);
      return channel;
    } catch (error) {
      console.error('Failed to listen for new messages:', error);
      return null;
    }
  }
}

// Create singleton instance
const pusherService = new PusherService();

export default pusherService;
